/**
 * Centralized business and UI constants for the HabitPack application.
 * Values here should represent shared logic, defaults, and magic numbers
 * pulled from feature modules to ensure consistency.
 */

export const PAGINATION = {
  /** Default page size for list views like search, notifications, logs */
  DEFAULT_PAGE_SIZE: 20,
  
  /** Number of items to show in preview/summary widgets */
  PREVIEW_LIMIT: 5,
} as const

export const APP_LIMITS = {
  /** Free-to-play limits for various entities */
  F2P: {
    HABITS: 3,
    GOALS: 2,
    PROJECTS: 1,
    TASKS: 50,
  } as const,
} as const

export const VALIDATION = {
  /** Maximum length for quick task titles */
  QUICK_TASK_TITLE_MAX_LENGTH: 80,
  
  /** Default quantitative habit fallback threshold (60 minutes/units target) */
  HABIT_FIXED_FALLBACK_THRESHOLD: 60,
} as const

export const CACHE = {
  /** Default TTL for non-critical cached data (5 minutes) */
  DEFAULT_TTL_MS: 5 * 60 * 1000,
} as const

export const ARENA = {
  /** Days allowed for players to register for a boss event */
  BOSS_REGISTRATION_WINDOW_DAYS: 7,
} as const

export const MISSIONS = {
  /** Prevent rapid sync requests (2 seconds) */
  SYNC_COOLDOWN_MS: 2000,
  
  /** Interval between polling for job completion (2 seconds) */
  SYNC_POLL_INTERVAL_MS: 2000,
  
  /** Max time to wait for a sync job (45 seconds) */
  SYNC_TIMEOUT_MS: 45000,
} as const

export const UI = {
  /** Default debounce delay for search inputs */
  SEARCH_DEBOUNCE_MS: 300,
  
  /** Standard animation durations */
  ANIMATION_MS: 300,

  /** 
   * Standard spring profiles for Framer Motion.
   * Synchronized to ensure a unified, premium feel across the application.
   */
  SPRINGS: {
    /** Satisfying settle with subtle follow-through - default for items, cards and staggers */
    PREMIUM: {
      type: 'spring',
      stiffness: 280,
      damping: 23,
      mass: 0.9,
      bounce: 0
    },
    /** More deliberate and grounded - good for layout changes, navigation and modals */
    GENTLE: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
      mass: 1,
      bounce: 0
    },
    /** Fast and energetic - good for micro-interactions, icons and feedback */
    ENERGY: {
      type: 'spring',
      stiffness: 500,
      damping: 25,
      mass: 0.5,
      bounce: 0
    }
  } as const,

  /** Standard color options for UI elements like habits or categories */
  COLOR_OPTIONS: [
    { label: 'Primaria', value: 'var(--primary)' },
    { label: 'Streak', value: 'var(--streak)' },
    { label: 'Sucesso', value: 'var(--success)' },
    { label: 'XP', value: 'var(--xp)' },
    { label: 'Coin', value: 'var(--coin)' },
    { label: 'Risco', value: 'var(--destructive)' },
  ],
} as const

export const EVENTS = {
  /** Dispatched when habits are created/updated/deleted to refresh views */
  HABITS_UPDATED: 'habitpack:habits-updated',
} as const
